import {
  pollForAccessToken,
  startPasswordlessLogin,
  TOKEN_POLL_TIMEOUT_MS,
} from '@paytrie/common/auth'
import { ArrowLeft, Mail, CheckCircle, Clock, RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { authService } from '@/services/auth'
import { logger } from '@/utils/logger'
import { validateEmail } from '@/utils/validation'

interface SignInScreenProps {
  onBack: () => void
  onSignIn: (token: string) => void
  onSignUp?: () => void
}

type AuthStep = 'email' | 'waiting' | 'checking' | 'expired' | 'denied'

const MAGIC_LINK_TIMEOUT_SECONDS = TOKEN_POLL_TIMEOUT_MS / 1000

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const SignInScreen = ({ onBack, onSignIn, onSignUp }: SignInScreenProps) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(MAGIC_LINK_TIMEOUT_SECONDS)
  const [emailError, setEmailError] = useState<string>('')
  const [authStep, setAuthStep] = useState<AuthStep>('email')

  const pollAbortRef = useRef<AbortController | null>(null)
  const activeSessionRef = useRef<{
    sessionId: string
    email: string
  } | null>(null)

  useEffect(() => {
    return () => pollAbortRef.current?.abort()
  }, [])

  const beginPoll = (session: { sessionId: string; email: string }) => {
    pollAbortRef.current?.abort()
    const controller = new AbortController()
    pollAbortRef.current = controller

    pollForAccessToken({
      sessionId: session.sessionId,
      timeoutMs: MAGIC_LINK_TIMEOUT_SECONDS * 1000,
      signal: controller.signal,
    })
      .then((result) => {
        // Terminal statuses consume the server-side session, so they must be
        // processed even if this controller was aborted mid-flight by a
        // concurrent visibility-triggered restart — otherwise we drop the
        // token and the restarted poll gets a 401 on the consumed link.
        if (result.status === 'verified') {
          activeSessionRef.current = null
          setAuthStep('checking')
          authService.setToken(result.accessToken, session.email)
          onSignIn(result.accessToken)
          return
        }
        if (result.status === 'denied') {
          activeSessionRef.current = null
          setAuthStep('denied')
          return
        }
        if (controller.signal.aborted) return
        if (!activeSessionRef.current) return
        activeSessionRef.current = null
        setAuthStep('expired')
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (!activeSessionRef.current) return
        activeSessionRef.current = null
        logger.error('Magic link poll failed', err)
        setAuthStep('expired')
      })
  }

  // Keep a latest-ref so the visibility handler always invokes the current
  // beginPoll closure without re-registering the DOM listener on every render.
  const beginPollRef = useRef(beginPoll)
  useEffect(() => {
    beginPollRef.current = beginPoll
  })

  // Mobile browsers pause background tabs, so the in-flight poll may be
  // stalled by the time the user returns from their email app. Restarting
  // on visibility=visible fires a fresh poll immediately.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && activeSessionRef.current) {
        beginPollRef.current(activeSessionRef.current)
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  useEffect(() => {
    if (authStep !== 'waiting' || timeRemaining <= 0) return

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          activeSessionRef.current = null
          pollAbortRef.current?.abort()
          setAuthStep('expired')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [authStep, timeRemaining])

  const sendMagicLink = async () => {
    const validation = validateEmail(email)
    if (!validation.isValid) {
      setEmailError(validation.error)
      return
    }

    setIsLoading(true)
    setError('')
    setEmailError('')
    pollAbortRef.current?.abort()

    try {
      const normalizedEmail = email.toLowerCase()
      const { sessionId } = await startPasswordlessLogin({
        email: normalizedEmail,
        client: 'gmt',
      })

      try {
        window.clarity?.('set', 'paytrie_session_id', sessionId)
      } catch {
        //
      }

      setAuthStep('waiting')
      setTimeRemaining(MAGIC_LINK_TIMEOUT_SECONDS)

      const session = {
        sessionId,
        email: normalizedEmail,
      }
      activeSessionRef.current = session
      beginPoll(session)
    } catch (err) {
      logger.error('Secure login link request failed', err)
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to send secure login link. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-[600px] flex flex-col dark:bg-gray-900">
      <div className="p-4 space-y-6 flex-1 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="p-1"
            disabled={isLoading}
          >
            <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              {authStep === 'email'
                ? 'Sign In'
                : authStep === 'denied'
                  ? 'Sign-in canceled'
                  : 'Check Your Email'}
            </h1>
          </div>
        </div>

        {/* Logo */}
        <div className="text-center py-2">
          <img
            src="/paytrie-logo-dark.png"
            alt="Paytrie"
            className="h-16 w-auto mx-auto dark:hidden"
          />
          <img
            src="/paytrie-logo.png"
            alt="Paytrie"
            className="h-16 w-auto mx-auto hidden dark:block"
          />
        </div>

        {/* Email Input Step */}
        {authStep === 'email' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) setEmailError('')
                  }}
                  placeholder="Enter your email"
                  className={`pl-10 dark:bg-gray-900 dark:border-gray-600 dark:text-white ${
                    emailError ? 'border-red-500 dark:border-red-500' : ''
                  }`}
                  disabled={isLoading}
                  onKeyDown={(e) => e.key === 'Enter' && sendMagicLink()}
                />
              </div>
            </div>

            {error && (
              <Card className="p-3 bg-red-50 border-red-200 dark:bg-red-900 dark:border-red-700">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </Card>
            )}

            <Button
              onClick={sendMagicLink}
              disabled={isLoading || !email.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sending Secure Login Link...</span>
                </div>
              ) : (
                'Send Secure Login Link'
              )}
            </Button>

            <Card className="p-3 bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                We'll send you a secure login link that expires in{' '}
                {MAGIC_LINK_TIMEOUT_SECONDS / 60} minutes.
              </p>
            </Card>
          </div>
        )}

        {authStep === 'denied' && (
          <div className="space-y-6">
            <Card className="p-6 text-center dark:bg-gray-800 dark:border-gray-700">
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  Sign-in canceled
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  If this was a mistake, you can try again below.
                </p>
              </div>
            </Card>
            <Button
              onClick={() => {
                setAuthStep('email')
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-sm font-medium rounded-xl"
            >
              Try again
            </Button>
          </div>
        )}

        {/* Waiting for Magic Link Click */}
        {(authStep === 'waiting' ||
          authStep === 'checking' ||
          authStep === 'expired') && (
          <div className="space-y-6">
            <Card
              className={`p-6 text-center ${authStep === 'expired' ? 'dark:bg-red-900 dark:border-red-700 bg-red-50 border-red-200' : 'dark:bg-gray-800 dark:border-gray-700'}`}
            >
              <div className="space-y-2">
                <div className="flex flex-col items-center justify-center h-12">
                  {authStep === 'expired' ? (
                    <div className="w-8 h-8 text-red-600 dark:text-red-400">
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  ) : authStep === 'checking' ? (
                    <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                  ) : (
                    <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  )}
                </div>

                {authStep === 'expired' ? (
                  <div className="h-7 flex items-center justify-center">
                    <p className="text-lg font-medium text-red-900 dark:text-red-100">
                      Link Expired
                    </p>
                  </div>
                ) : null}

                <div className="space-y-2 pt-0">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {authStep === 'expired'
                      ? 'The secure login link we sent to:'
                      : 'We sent a secure login link to:'}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded">
                    {email}
                  </p>
                </div>

                {authStep === 'expired' ? (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-red-600 dark:text-red-400">
                      This link has expired
                    </div>
                    <div className="text-sm text-red-700 dark:text-red-300">
                      Please click "Resend Secure Login Link" below to get a new
                      link
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Link expires in:{' '}
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                )}
              </div>
            </Card>

            {authStep !== 'expired' && (
              <Card className="p-4 bg-green-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
                <div className="space-y-1">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4  mt-0.5 flex-shrink-0" />
                    <p className="text-xs">
                      Check your email inbox and spam folder
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p className="text-xs">
                      Click the "Sign In" button in the email to finish here
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="space-y-2 pb-2">
              <Button
                variant="outline"
                onClick={sendMagicLink}
                disabled={isLoading}
                className={`w-full ${authStep === 'expired' ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20' : ''}`}
              >
                Resend Secure Login Link
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="text-center p-4 border-t border-gray-100 dark:border-gray-700 dark:bg-gray-900">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Don't have an account?
          <button
            onClick={onSignUp || onBack}
            className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 ml-1"
            disabled={isLoading}
          >
            Sign up here
          </button>
        </p>
      </div>
    </div>
  )
}

export default SignInScreen
